import manager from 'abstract-storage';

const errorCodes = manager.errorCodes;
const SEP = '.';

const NSKEYS = 'ds::';
const UNDEF = undefined;

let hasLocalStorage = true;

try {
    if (localStorage.getItem) {
        hasLocalStorage = true;
    }
} catch (e) {
    hasLocalStorage = false;
}

const getEmpty = manager.getEmpty();

/**
 * Retrieve from localStorage using the merged namespace+SEP+key value.
 * @param  {string} fullkey namespace+SEP+key
 * @return {object}         StorageEntry instance or empty storage object.
 */
const getWithFullKey = (fullkey) => {
    const str = hasLocalStorage && localStorage.getItem(fullkey);

    if (!str) {
        return manager.getEmpty();
    }

    const storable = hydrateStoredItem(str);

    return manager.get(storable);
};

/**
 * Serialize the storageEntry's meta data fields to compress stored object a bit
 * @param  {StorageEntry} item StorageEntry instance to be serialized
 * @return {[type]}      [description]
 */
const serializeMetaData = (item) => {
    const meta = [item.namespace, item.key, item.expires, item.created, item.expired, item.value];

    return JSON.stringify(meta);
};

const hydrateStoredItem = (itemString) => {
    const item = JSON.parse(itemString);
    let storable;
    // Old stored items might not have been serialized
    if (Array.isArray(item)) {
        storable = manager.storable(item[0], item[1], item[5], item[2]);
        storable.created = item[3];
        storable.expired = item[4];
    } else {
        storable = manager.storable(item.namespace, item.key, item.value, item.expires);
        storable.created = item.created;
        // Older stored items might have expired=-1, which has been changed to 'false' internally
        storable.expired = item.expired === -1 ? false : item.expired;
    }
    return storable;
};

const store = (storageEntry, skipRetry) => {
    if (hasLocalStorage && storageEntry.namespace && storageEntry.created !== 0) {
        try {
            localStorage.setItem(NSKEYS + storageEntry.namespace + SEP + storageEntry.key, serializeMetaData(storageEntry));
        } catch (storeError) {
            if (skipRetry === true) {
                return storeError.message;
            }
            // Looks like local storage is full for the domain.
            // Try cleaning up expired items, and give it one more shot.
            storageCleanup(storageEntry.namespace, true);
            return store(storageEntry, true);
        }
        return true;
    }
    return false;
};

const storageCleanup = (namespace, onlyExpired) => {
    let i = 0;
    let key;
    const cleaned = {};
    let item;
    // If namespace == undef - match all keys
    const nsKey = namespace !== UNDEF ? nsAsKey(namespace) : NSKEYS;

    if (hasLocalStorage) {
        for (;i < localStorage.length; i++) {
            key = localStorage.key(i);
            if (key.indexOf(nsKey) === 0) {
                item = getWithFullKey(key);
                if (onlyExpired === false || (onlyExpired === true && item.expired === 1)) {
                    localStorage.removeItem(key);
                    i--;
                    cleaned[item.key] = item;
                }
            }
        }
    }
    return cleaned;
};

const fullStorageCleanup = (onlyExpired) => {
    return storageCleanup(UNDEF, onlyExpired);
};

const nsAsKey = (namespace) => {
    return NSKEYS + namespace + SEP;
};

const getAllFromNamespace = (matches) => {
    const nso = {};
    let key;
    let item;
    let namespace;
    const len = hasLocalStorage ? localStorage.length : 0;
    let i = 0;

    for (; i < len; i++) {
        key = localStorage.key(i);
        if ((key + '').indexOf(matches) === 0) {
            item = getWithFullKey(key);
            namespace = item.namespace;
            if (!nso[namespace]) {
                nso[namespace] = {};
            }
            nso[namespace][item.key] = item;
        }
    }
    return nso;
};
export default {
    StorageEntry: manager.StorageEntry,
    /**
         * Create a new StorageEntry instance and place it in the browsers
         * localStorage cache.
         *
         * @param  {string} namespace Top-level container for object grouping.
         * @param  {string} key       Primary storage key for individial objects.
         * @param  {mixed}  value     Data to be stored.
         * @param  {number} expires   Time in seconds
         * @return {object}           Returns a valid StorageEntry instance.
         */
    put: (namespace, key, value, expires) => {
        let stored;
        if (manager.validKeys(namespace, key)) {
            const item = manager.storable(namespace, key, value, expires);
            stored = store(item);
            if (stored === true) {
                return item;
            }
        }
        return getEmpty(stored);
    },
    /**
         * Place an exiting StorageEntry instance in localStorage.
         *
         * @param  {object} storageEntry An instance of StorageEntry.
         * @return {boolean}            Returns true if storageEntry was successfully adopted.
         */
    adopt: (storageEntry) => {
        if (!(storageEntry instanceof manager.StorageEntry)) {
            return false;
        }
        return store(storageEntry);
    },
    /**
         * Attempt to retrieve all items in the localStorage cache, and if a namespace is passed, under the
         * provided namespace.
         * @param  {string} [namespace] Namespace to retrieve, or pass no namespace to get all namespaces.
         * @return {object}             Collection of retrieved items or empty object.
         */
    getAll: (namespace) => {
        if (namespace !== UNDEF) {
            return getAllFromNamespace(nsAsKey(namespace))[namespace] || {};
        }
        return getAllFromNamespace(NSKEYS);
    },
    /**
         * Attempt to retrieve a storage object from the localStoage cache items.
         * If expired as dicated by the created and expires properties,
         * the object will be marked accordingly via the expired property.
         * (If none exists, return an empty expired object)
         *
         * @param {string} namespace Namespace of object to retrieve.
         * @param {string} key      Key of object to retrieve from the given namespace.
         * @uses   getWithFullKey
         * @return {object}     Object so or an "empty" storage object.
         */
    get: (namespace, key) => {
        if (manager.validKeys(namespace, key)) {
            return getWithFullKey(nsAsKey(namespace) + key);
        }
        return manager.getEmpty(errorCodes.INVALID_KEY);
    },
    /**
         * Mark a storage object as expired.
         * @param {string} namespace Namespace of object to expire.
         * @param {string} key      Key of object to expire.
         */
    expire: function (namespace, key) {
        let item;
        if (manager.validKeys(namespace, key)) {
            item = this.get(namespace, key);
        } else {
            item = manager.getEmpty(errorCodes.INVALID_KEY);
        }
        manager.expire(item);
        if (item.created !== 0) {
            this.adopt(item);
        }
        return item;
    },
    /**
         * Remove object key from object namespace and the cache.
         * @param  {string} namespace Namespace of targeted object.
         * @param  {string} key       Key of target object.
         * @return {StorageEntry}     Removed item.
         */
    delete: function (namespace, key) {
        const item = this.get(namespace, key);
        if (hasLocalStorage) {
            localStorage.removeItem(nsAsKey(namespace) + key);
        }
        return item;
    },
    /**
         * Remove the given namespace and it's objects from the cache.
         * @param  {string} namespace Namespace of targeted object.
         */
    clear: (namespace) => {
        if (namespace !== UNDEF) {
            return storageCleanup(namespace, false);
        }
        return {};
    },
    /**
         * Remove all namespaces and their objects from the cache.
         */
    clearAll: () => {
        return fullStorageCleanup(false);
    },
    /**
         * Remove expired objects from the given namespace.
         * @param  {string} namespace Namespace of targeted object.
         */
    clean: (namespace) => {
        if (namespace !== UNDEF) {
            return storageCleanup(namespace, true);
        }
        return {};
    },
    /**
         * Remove the expired objects from the all namespaces.
         * @param  {string} namespace Namespace to clear.
         * @return {object} Object composed of all of the removed elements.
         */
    cleanAll: () => {
        return fullStorageCleanup(true);
    },

    isActive: hasLocalStorage
};
